import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState, SkeletonRows, EmptyState } from '@/components/ui/Panel';
import { useFleetIntegrity } from '@/hooks/useFleetIntegrity';

/**
 * The "fleet integrity" dashboard: every automated integrity/GPS alert this
 * system produces (unmatched GPS visits, unexplained repeat stops,
 * odometer/GPS mismatches, dwell/weight mismatches, competitor-quarry
 * diversions, service reminders, shadow driving, elevated breakdown risk),
 * rolled up into ONE row per vehicle and ONE row per driver — see
 * blueprints/reporting.py's fleet_integrity() docstring for exactly what
 * each column means and how it's computed. Answers "which truck/driver
 * most needs a look right now" at a glance, instead of a dispatcher piecing
 * that together from separate notifications-bell lines. Opened from the
 * Transportas toolbar (VehicleGrid.jsx), same open/close/loading/error/
 * empty-state pattern as GpsFleetReportModal.jsx.
 */
export function FleetIntegrityModal({ open, onClose }) {
  const t = useT();
  const { data, isLoading, isError, error } = useFleetIntegrity();
  const vehicles = data?.vehicles ?? [];
  const drivers = data?.drivers ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('fleetIntegrity.title')}
      width="max-w-[920px]"
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('common.closeBtn')}
        </button>
      }
    >
      <div className="space-y-4">
        {isError ? <ErrorState error={error} /> : null}
        {isLoading ? <SkeletonRows rows={6} /> : null}

        {!isLoading && data ? (
          <>
            <p className="text-[11px] text-dim">{t('fleetIntegrity.windowNote', { days: data.window_days })}</p>

            <div>
              <h4 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
                {t('fleetIntegrity.vehiclesHeading')}
              </h4>
              {vehicles.length === 0 ? (
                <EmptyState>{t('fleetIntegrity.noVehicles')}</EmptyState>
              ) : (
                <VehicleTable vehicles={vehicles} t={t} />
              )}
            </div>

            <div>
              <h4 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
                {t('fleetIntegrity.driversHeading')}
              </h4>
              {drivers.length === 0 ? (
                <EmptyState>{t('fleetIntegrity.noDrivers')}</EmptyState>
              ) : (
                <DriverTable drivers={drivers} t={t} />
              )}
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function VehicleTable({ vehicles, t }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[11px]">
        <thead>
          <tr className="text-dim">
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.risk')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.plate')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.alerts')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.total')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.gpsConfirmed')}</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((v) => (
            <tr key={v.vehicle_id} className="border-t border-line align-top">
              <td className="px-2 py-1.5">
                <div className="flex items-center gap-1">
                  <RiskBadge level={v.risk_level} score={v.risk_score} t={t} />
                  <TrendIndicator trend={v.risk_trend} direction={v.trend_direction} t={t} />
                </div>
              </td>
              <td className="px-2 py-1.5 font-semibold text-ink">{v.license_plate}</td>
              <td className="px-2 py-1.5">
                <AlertBadges counts={v.counts} t={t} />
              </td>
              <td className="px-2 py-1.5">
                <TotalBadge total={v.total_worth_checking} />
              </td>
              <td className="px-2 py-1.5">
                <RateBadge rate={v.gps_confirmed_delivery_rate} t={t} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DriverTable({ drivers, t }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[11px]">
        <thead>
          <tr className="text-dim">
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.risk')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.driver')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.alerts')}</th>
            <th className="px-2 py-1 font-semibold">{t('fleetIntegrity.col.total')}</th>
          </tr>
        </thead>
        <tbody>
          {drivers.map((d) => (
            <tr key={d.driver_id} className="border-t border-line align-top">
              <td className="px-2 py-1.5">
                <div className="flex items-center gap-1">
                  <RiskBadge level={d.risk_level} score={d.risk_score} t={t} />
                  <TrendIndicator trend={d.risk_trend} direction={d.trend_direction} t={t} />
                </div>
              </td>
              <td className="px-2 py-1.5 font-semibold text-ink">{d.full_name}</td>
              <td className="px-2 py-1.5">
                <AlertBadges counts={d.counts} t={t} />
              </td>
              <td className="px-2 py-1.5">
                <TotalBadge total={d.total_worth_checking} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The one consolidated number this whole dashboard exists to surface — see
 * blueprints/reporting.py's compute_fleet_integrity_risk_score() /
 * FLEET_INTEGRITY_CATEGORY_WEIGHTS for how `risk_score` is weighted and how
 * `risk_level` buckets it. Deliberately the FIRST column and the most
 * visually loud thing in either table (solid fill, not just a tinted
 * badge like AlertBadges below) — a dispatcher should be able to tell which
 * rows need attention without reading a single count, which is the whole
 * point of adding a consolidated score instead of nine separate ones.
 */
function RiskBadge({ level, score, t }) {
  const tone =
    level === 'high' ? 'bg-red text-white' : level === 'medium' ? 'bg-amber text-white' : 'bg-green/15 text-green';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${tone}`}>
      {t(`fleetIntegrity.risk.${level}`)} · {score}
    </span>
  );
}

/**
 * The trend read-out next to RiskBadge: whether this vehicle/driver's
 * risk_score is climbing, falling, or flat over the last several weekly
 * snapshots (blueprints/reporting.py's fleet_integrity() `risk_trend`/
 * `trend_direction` — see schema.sql's fleet_integrity_snapshots and
 * app.py's take_fleet_integrity_snapshot_for_tenant()). Deliberately small
 * and secondary — a plain glyph, no fill, no pill — so it never competes
 * with RiskBadge for a dispatcher's eye; it's a "worth a second look"
 * signal, not the headline. Renders nothing at all (not even a dash) for
 * fewer than 2 snapshots: a row with no trend yet shouldn't visually imply
 * "flat," it should just not claim a trend exists. The tooltip carries the
 * raw score history so the badge stays legible without cluttering the row.
 */
function TrendIndicator({ trend, direction, t }) {
  if (!direction || !trend?.length) return null;
  const glyph = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '—';
  const tone = direction === 'up' ? 'text-red' : direction === 'down' ? 'text-green' : 'text-dim';
  const history = trend.map((p) => `${p.snapshot_date}: ${p.risk_score}`).join('\n');
  return (
    <span
      className={`text-[10px] font-bold leading-none ${tone}`}
      title={`${t(`fleetIntegrity.trend.${direction}`)}\n${history}`}
    >
      {glyph}
    </span>
  );
}

/**
 * A diversion to a competitor's quarry is shown in red regardless of its
 * count — it's the one category on this dashboard that names a specific
 * suspicious act rather than a data anomaly or a maintenance signal, same
 * severity="critical" list_notifications() (blueprints/reporting.py) gives
 * it in the bell feed. Every other non-zero category is amber; a row with
 * nothing to show renders a plain dash rather than an empty cell, so "no
 * badges" reads as "checked, nothing found" rather than "still loading."
 */
function AlertBadges({ counts, t }) {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) return <span className="text-dim">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([key, n]) => (
        <span
          key={key}
          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
            key === 'gps_diversion_alert' ? 'bg-red/15 text-red' : 'bg-amber/15 text-amber'
          }`}
        >
          {t(`fleetIntegrity.cat.${key}`)}: {n}
        </span>
      ))}
    </div>
  );
}

function TotalBadge({ total }) {
  const tone = total === 0 ? 'text-dim' : total <= 2 ? 'text-amber' : 'text-red';
  return <span className={`text-[13px] font-bold ${tone}`}>{total}</span>;
}

/**
 * Same shape and same reasoning as ClientReliabilityModal's own RateBadge:
 * null means "no deliveries to measure yet," not "0% confirmed," so it gets
 * its own neutral label instead of a misleadingly red 0%.
 */
function RateBadge({ rate, t }) {
  if (rate === null || rate === undefined) {
    return <span className="text-dim">{t('clientReliability.noData')}</span>;
  }
  const pct = Math.round(rate * 100);
  const tone = pct >= 80 ? 'text-green' : pct >= 40 ? 'text-amber' : 'text-red';
  return <span className={`font-semibold ${tone}`}>{pct}%</span>;
}
